import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { LANG_CODES } from './LanguageRouter';

// Production domain - ALWAYS use this for canonical URLs
const LIVE_DOMAIN = 'https://www.towertech-xm.com';

// Preview/staging domains that should NOT be indexed
const NOINDEX_DOMAINS = [
  'preview.emergentagent.com',
  'emergent.sh',
  'localhost',
  '127.0.0.1'
];

const SEO = ({ 
  title = "Xiamen Tower Technology - Import & Export Solutions",
  description = "Your trusted partner for import-export and product sourcing from China. We specialize in connecting international businesses with reliable Chinese manufacturers.",
  keywords = "import from china, export china, product sourcing, chinese manufacturers, import export, xiamen tower technology",
  image = `${LIVE_DOMAIN}/og-image.jpg`,
  type = "website"
}) => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  // Determine if current domain should be noindexed
  const isPreviewDomain = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const currentHost = window.location.hostname;
    return NOINDEX_DOMAINS.some(domain => currentHost.includes(domain));
  }, []);

  // Strip leading language segment from the path so we can build alternates per lang
  const pathWithoutLang = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length > 0 && LANG_CODES.includes(segments[0])) {
      return '/' + segments.slice(1).join('/');
    }
    return location.pathname;
  }, [location.pathname]);

  const buildLangUrl = (lang) => {
    const sub = pathWithoutLang === '/' || pathWithoutLang === '' ? '' : pathWithoutLang;
    return `${LIVE_DOMAIN}/${lang}${sub}`;
  };

  // ALWAYS use live domain for canonical URL, with language prefix
  const canonicalUrl = buildLangUrl(currentLanguage || 'en');

  // Robots directive: noindex for preview domains, index for production
  const robotsContent = isPreviewDomain ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLanguage || 'en'} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* CRITICAL: Robots directive - noindex for preview domains */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      
      {/* CRITICAL: Canonical URL - ALWAYS points to live domain w/ language prefix */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook - Always use live domain */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={currentLanguage === 'en' ? 'en_US' : currentLanguage === 'de' ? 'de_DE' : currentLanguage === 'es' ? 'es_ES' : currentLanguage === 'sl' ? 'sl_SI' : 'sr_RS'} />
      <meta property="og:site_name" content="Xiamen Tower Technology" />
      
      {/* Twitter - Always use live domain */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      {/* Additional SEO */}
      <meta name="language" content={currentLanguage || 'English'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Xiamen Tower Technology" />
      
      {/* Alternate language versions - path-prefixed live domain URLs */}
      <link rel="alternate" hrefLang="en" href={buildLangUrl('en')} />
      <link rel="alternate" hrefLang="de" href={buildLangUrl('de')} />
      <link rel="alternate" hrefLang="es" href={buildLangUrl('es')} />
      <link rel="alternate" hrefLang="sl" href={buildLangUrl('sl')} />
      <link rel="alternate" hrefLang="sr" href={buildLangUrl('sr')} />
      <link rel="alternate" hrefLang="x-default" href={buildLangUrl('en')} />
    </Helmet>
  );
};

export default SEO;
